// ===========================================
// Class weeks — which weeks of its term a PLAIN class runs (2026-10-15)
// ===========================================
// The frontend mirror of the backend's `utils/classWeeks.ts`: identical names and
// bodies, pinned by the shared case table `apps/backend/tests/fixtures/classWeeksCases.json`
// (classWeeks.agreement.test.ts). A plain class (INDIVIDUAL, GROUP or BAND) may run in a
// CUSTOM set of its term's weeks, stored as a `LessonTermWeeks` row; no row, EVERY_WEEK, or
// anything unreadable means every week of the class window. Read AFTER the window and AFTER
// a caller's own HYBRID branch. Plan: md/plan/individual-group-week-selection.md (D4, D13).

import { weekInLessonWindow, type LessonWindowSource } from './dateUtils';

/** One stored week row, as `LessonTermWeeks` selects it. */
export interface ClassWeeksRow {
  termId: string;
  patternType: string;
  weeks: unknown;
}

/**
 * The fields the rule reads. `LessonType` and `LessonTermWeeks` are REQUIRED (nullable):
 * a select that forgot either would otherwise compile and silently read "every week"
 * (the `include: { Stream }` trap). Pass `null` only when the caller KNOWS there is none.
 */
export interface ClassWeeksSource extends LessonWindowSource {
  termId: string;
  LessonType: { type: string } | null;
  LessonTermWeeks: ReadonlyArray<ClassWeeksRow> | null;
}

/**
 * The stored row for the lesson's OWN term, whatever its pattern — `null` when there is
 * none (or the class is HYBRID). For the one caller that must tell "no row" from "a row
 * that reads as every week": the roll's partner-band fallback applies only with no row.
 */
export function ownClassWeeksRow(lesson: ClassWeeksSource): ClassWeeksRow | null {
  if (lesson.LessonType?.type === 'HYBRID') return null;
  return lesson.LessonTermWeeks?.find((r) => r.termId === lesson.termId) ?? null;
}

/**
 * The stored CUSTOM list for the lesson's OWN term: positive integers, sorted and
 * de-duplicated. `null` — every week — for a HYBRID class, no row, a row for another
 * term, an EVERY_WEEK row, or a list that is empty or unreadable once cleaned.
 */
export function storedClassWeeks(lesson: ClassWeeksSource): number[] | null {
  const row = ownClassWeeksRow(lesson);
  if (!row || row.patternType !== 'CUSTOM' || !Array.isArray(row.weeks)) return null;
  const weeks = [...new Set(row.weeks.filter((w): w is number => Number.isInteger(w) && w >= 1))];
  return weeks.length > 0 ? weeks.sort((a, b) => a - b) : null;
}

/**
 * The weeks a plain class has a session: its stored list inside the class window.
 * `null` means every week of the window; `[]` means NO session.
 */
export function classSessionWeeks(lesson: ClassWeeksSource, termWeeks: number): number[] | null {
  const stored = storedClassWeeks(lesson);
  if (stored === null) return null;
  return stored.filter((week) => weekInLessonWindow(week, lesson, termWeeks));
}

/**
 * Does the plain class have a session in term week `week`? Window first, then the list.
 * ⚠️ Argument order is (lesson, week, termWeeks) — like the `lessonRunsInWeek` copies it
 * replaces, NOT like `weekInLessonWindow(week, lesson, termWeeks)`. Both numbers are bare,
 * so a swap compiles: name the arguments at the call site.
 */
export function classRunsInWeek(lesson: ClassWeeksSource, week: number, termWeeks: number): boolean {
  if (!weekInLessonWindow(week, lesson, termWeeks)) return false;
  const stored = storedClassWeeks(lesson);
  return stored === null || stored.includes(week);
}
